import React, { useEffect, useState } from 'react';
import {
  SIA_FOLLOWUP_COMPLETED_NOTIF_TYPE,
  SIA_FOLLOWUP_COMPLETED_NOTIF_URL_PARAM,
  SIA_PERSONAL_LINES_COMPLETED_NOTIF_TYPE,
  SIA_PERSONAL_LINES_COMPLETED_NOTIF_URL_PARAM,
  SIA_PERSONALIZATION_FAILED_REASON,
} from '@/utils/constants/sia';
import { NOTIF_TYPES } from '@/utils/constants/notifications';
import {
  SIA_PERSONALIZATION_COMPLETED_NOTIF_TYPE,
  SIA_PERSONALIZATION_FAILED_NOTIF_TYPE,
} from '@Saral-Influencer-Marketing/saral-utils/src/constants/notification';
import { isFollowupsModalOpen } from '@/utils/followups-modal-utils';
import { isPersonalLinesModalOpen } from '@/utils/personal-lines-modal-utils';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';

import { fetchNotifications } from '../../../redux/notifications/notificationActions';
import { updateSeenStatus } from '../../../services/notifications.service';
import CustomNotification from '../CustomNotification/CustomNotification';
import { getSocialCampaignDetailRoute } from '@/config/baseRoutes';
import SathiMigrationCompletedCustomNotification from './SathiNotifications/SathiMigrationCompletedCustomNotification';
import SathiMigrationInterruptedCustomNotification from './SathiNotifications/SathiMigrationInterruptedCustomNotification';

// Key for localStorage
const LAST_NOTIFICATION_TIME_KEY = 'saral_last_notification_time';

export default function NotificationsProvider({ children }) {
  const showNotificationDefault = {
    postTracking: false,
    siaPersonalizationSuccess: false,
    siaPersonalizationFailed: false,
    listPersonalizationSuccess: false,
    sathiMigrationCompleted: false,
    sathiMigrationInterrupted: false,
  };

  const [showNotification, setShowNotification] = useState(showNotificationDefault);

  const [alreadyShownNotifications, setAlreadyShownNotifications] = useState(showNotificationDefault);

  const [currentNotifications, setCurrentNotifications] = useState([]);
  const [groupedNotifications, setGroupedNotifications] = useState([]);

  const [siaPersonalizationFailedNotification, setSiaPersonalizationFailedNotification] = useState(null);
  const [personalizationSuccessNotification, setPersonalizationSuccessNotification] = useState(null);
  const [sathiMigrationCompletedNotification, setSathiMigrationCompletedNotification] = useState(null);
  const [sathiMigrationInterruptedNotification, setSathiMigrationInterruptedNotification] = useState(null);

  const { notifications } = useSelector((state) => state.notifications);

  const dispatch = useDispatch();

  const handleUpdateShowNotification = (type, value) => {
    setShowNotification((prev) => ({
      ...prev,
      [type]: value,
    }));
  };

  const handleUpdateAlreadyShownNotification = (type, value) => {
    setAlreadyShownNotifications((prev) => ({
      ...prev,
      [type]: value,
    }));
  };

  useEffect(() => {
    // Only process if we're not already showing a notification
    if (
      showNotification.siaPersonalizationSuccess ||
      showNotification.siaPersonalizationFailed ||
      alreadyShownNotifications.siaPersonalizationSuccess ||
      alreadyShownNotifications.siaPersonalizationFailed
    )
      return;

    const now = new Date();
    const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);

    // Check if 5 minutes have passed since the last notification using localStorage
    const currentTime = now.getTime();
    const lastNotificationTime = parseInt(localStorage.getItem(LAST_NOTIFICATION_TIME_KEY) || '0', 10);
    const timeSinceLastNotification = currentTime - lastNotificationTime;
    const fiveMinutesInMs = 5 * 60 * 1000;

    const unreadSiaSuccessNotifs = notifications.filter(
      (notification) =>
        notification.type === SIA_PERSONALIZATION_COMPLETED_NOTIF_TYPE &&
        !notification.seen &&
        dayjs(notification.createdAt).isAfter(fiveMinutesAgo)
    );

    if (unreadSiaSuccessNotifs.length > 0) {
      handleUpdateShowNotification('siaPersonalizationSuccess', true);
      handleUpdateAlreadyShownNotification('siaPersonalizationSuccess', true);
    }

    const unreadSiaFailedNotif = notifications.find(
      (notification) =>
        notification.type === SIA_PERSONALIZATION_FAILED_NOTIF_TYPE &&
        !notification.seen &&
        new Date(notification.createdAt) > fiveMinutesAgo
    );

    if (unreadSiaFailedNotif) {
      handleUpdateShowNotification('siaPersonalizationFailed', true);
      setSiaPersonalizationFailedNotification(unreadSiaFailedNotif);
      handleUpdateAlreadyShownNotification('siaPersonalizationFailed', true);
    }

    if (showNotification.listPersonalizationSuccess || alreadyShownNotifications.listPersonalizationSuccess) return;

    const unreadListPersonalizationSuccessNotif = notifications.find(
      (notification) =>
        (notification.type === SIA_PERSONAL_LINES_COMPLETED_NOTIF_TYPE ||
          notification.type === SIA_FOLLOWUP_COMPLETED_NOTIF_TYPE) &&
        !notification.seen
    );

    if (unreadListPersonalizationSuccessNotif) {
      const isModalOpen = isPersonalLinesModalOpen() || isFollowupsModalOpen();
      if (!isModalOpen) {
        handleUpdateShowNotification('listPersonalizationSuccess', true);
        setPersonalizationSuccessNotification(unreadListPersonalizationSuccessNotif);
        handleUpdateAlreadyShownNotification('listPersonalizationSuccess', true);
      }
    }

    const unreadSathiMigrationInterruptedNotif =
      !showNotification.sathiMigrationInterrupted && !alreadyShownNotifications.sathiMigrationInterrupted
        ? notifications.find(
            (notification) => notification.type === NOTIF_TYPES.SATHI_MIGRATION_INTERRUPTED && !notification.seen
          )
        : null;

    if (unreadSathiMigrationInterruptedNotif) {
      handleUpdateShowNotification('sathiMigrationInterrupted', true);
      setSathiMigrationInterruptedNotification(unreadSathiMigrationInterruptedNotif);
      handleUpdateAlreadyShownNotification('sathiMigrationInterrupted', true);
    } else if (!showNotification.sathiMigrationCompleted && !alreadyShownNotifications.sathiMigrationCompleted) {
      const unreadSathiMigrationCompletedNotif = notifications.find(
        (notification) => notification.type === NOTIF_TYPES.SATHI_MIGRATION_COMPLETED && !notification.seen
      );

      if (unreadSathiMigrationCompletedNotif) {
        handleUpdateShowNotification('sathiMigrationCompleted', true);
        setSathiMigrationCompletedNotification(unreadSathiMigrationCompletedNotif);
        handleUpdateAlreadyShownNotification('sathiMigrationCompleted', true);
      }
    }

    if (showNotification.postTracking) return;

    if (timeSinceLastNotification < fiveMinutesInMs) {
      // Less than 5 minutes have passed, don't show a notification
      return;
    }

    // Filter for unseen postTracking notifications
    const unreadPostTrackingNotifications = notifications.filter(
      (notification) =>
        notification.type === 'postTracking' && !notification.seen && new Date(notification.createdAt) > fiveMinutesAgo
    );

    // If we have notifications to show
    if (unreadPostTrackingNotifications.length > 0) {
      // Group notifications by influencer ID
      const grouped = groupNotificationsByInfluencer(unreadPostTrackingNotifications);

      // Update the last notification time in localStorage
      localStorage.setItem(LAST_NOTIFICATION_TIME_KEY, currentTime.toString());

      setCurrentNotifications(unreadPostTrackingNotifications);
      setGroupedNotifications(grouped);
      handleUpdateShowNotification('postTracking', true);

      // Mark notifications as read as soon as they're shown
      markNotificationsAsRead(unreadPostTrackingNotifications);
    }
  }, [
    notifications,
    showNotification.postTracking,
    showNotification.siaPersonalizationSuccess,
    showNotification.siaPersonalizationFailed,
    showNotification.listPersonalizationSuccess,
    showNotification.sathiMigrationCompleted,
    showNotification.sathiMigrationInterrupted,
    alreadyShownNotifications.siaPersonalizationSuccess,
    alreadyShownNotifications.siaPersonalizationFailed,
    alreadyShownNotifications.listPersonalizationSuccess,
    alreadyShownNotifications.sathiMigrationCompleted,
    alreadyShownNotifications.sathiMigrationInterrupted,
  ]);

  // Group notifications by influencer ID
  const groupNotificationsByInfluencer = (notifications) => {
    const groups = {};

    notifications.forEach((notification) => {
      const influencerId = notification.influencerId?._id;
      if (!influencerId) return;

      if (!groups[influencerId]) {
        groups[influencerId] = {
          influencer: notification.influencerId,
          content: notification.content,
          notifications: [],
        };
      }

      groups[influencerId].notifications.push(notification);
    });

    // Convert to array and sort by number of posts (descending)
    return Object.values(groups).sort((a, b) => b.notifications.length - a.notifications.length);
  };

  // Function to mark notifications as read
  const markNotificationsAsRead = async (notificationsToMark) => {
    const notificationIds = notificationsToMark?.map((notification) => notification._id);

    try {
      await updateSeenStatus({ notificationIds });
      dispatch(fetchNotifications());
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error marking notifications as seen:', error);
    }
  };

  const handleCloseNotification = () => {
    setShowNotification(showNotificationDefault);
    setCurrentNotifications([]);
    setGroupedNotifications([]);
    setSiaPersonalizationFailedNotification(null);
    setPersonalizationSuccessNotification(null);
    setSathiMigrationCompletedNotification(null);
    setSathiMigrationInterruptedNotification(null);
  };

  const handleActionClick = () => {
    if (currentNotifications.length > 0) {
      // If there are grouped notifications from the same influencer, use the campaign ID from the first one
      const campaignId = currentNotifications[0]?.content?.campaignId;
      window.location.href = getSocialCampaignDetailRoute(campaignId);
    }
    handleCloseNotification();
  };

  const getNotificationAvatars = () => {
    // If all notifications are from the same influencer, just return one avatar
    if (groupedNotifications.length === 1) {
      return [groupedNotifications[0].content.profileImg || groupedNotifications[0].influencer.profile_pic];
    }

    // Otherwise return avatars for the first two different influencers
    return groupedNotifications.slice(0, 2).map((group) => group.content.profileImg || group.influencer.profile_pic);
  };

  const renderNotificationMessage = () => {
    // Case 1: All notifications are from the same influencer
    if (groupedNotifications.length === 1 && groupedNotifications[0].notifications.length > 1) {
      const { influencer, notifications } = groupedNotifications[0];
      const name = influencer.name || influencer.first_name;

      return (
        <p>
          <b className="font-medium">{name}</b> has mentioned you in {notifications.length} posts.
        </p>
      );
    }

    // Case 2: Single notification
    if (currentNotifications.length === 1) {
      const { content } = currentNotifications[0];
      return (
        <p>
          <b className="font-medium">{content.fullName}</b> has mentioned you in their Post.
        </p>
      );
    }

    // Case 3: Two different influencers
    else if (groupedNotifications.length === 2) {
      const first = groupedNotifications[0].influencer.first_name || groupedNotifications[0].influencer.name;
      const second = groupedNotifications[1].influencer.first_name || groupedNotifications[1].influencer.name;

      return (
        <p>
          <b className="font-medium">{first}</b> and <b className="font-medium">{second}</b> have mentioned you in their
          Posts.
        </p>
      );
    }

    // Case 4: More than two different influencers
    else {
      const first = groupedNotifications[0].influencer.first_name || groupedNotifications[0].influencer.name;
      const second = groupedNotifications[1].influencer.first_name || groupedNotifications[1].influencer.name;
      const othersCount = groupedNotifications.length - 2;

      return (
        <p>
          <b className="font-medium">{first}</b>, <b className="font-medium">{second}</b> and{' '}
          <b className="font-medium">{othersCount}</b> others have mentioned you in their Posts.
        </p>
      );
    }
  };

  return (
    <>
      <audio id="notification-sound" src="/audio/notification-bell.mp3" preload="auto" />
      {children}
      {showNotification.postTracking && (
        <CustomNotification
          open={showNotification.postTracking}
          onClose={handleCloseNotification}
          title="📣 New Mentions!"
          message={renderNotificationMessage()}
          actionText="Check it Out!"
          avatars={getNotificationAvatars()}
          autoHideDuration={6000} // 6 seconds
          instantClose={true}
          playSound={true}
          onActionClick={handleActionClick}
        />
      )}
      {showNotification.siaPersonalizationSuccess && (
        <CustomNotification
          open={showNotification.siaPersonalizationSuccess}
          onClose={handleCloseNotification}
          title="✨ SIA's picked up your tone!"
          message="SIA has absorbed your guidelines & can now generate personalised responses to your emails."
          actionText="Try it Now"
          autoHideDuration={6000}
          instantClose={true}
          playSound={true}
          handleClickCross={() => {
            const unreadSiaSuccessNotifs = notifications.filter(
              (notification) => notification.type === SIA_PERSONALIZATION_COMPLETED_NOTIF_TYPE && !notification.seen
            );
            markNotificationsAsRead(unreadSiaSuccessNotifs);
            handleCloseNotification();
          }}
          onActionClick={() => {
            const unreadSiaSuccessNotifs = notifications.filter(
              (notification) => notification.type === SIA_PERSONALIZATION_COMPLETED_NOTIF_TYPE && !notification.seen
            );
            if (unreadSiaSuccessNotifs.length > 0) {
              markNotificationsAsRead(unreadSiaSuccessNotifs).then(() => {
                window.location.href = '/dashboard/inbox';
              });
            }
            window.location.href = '/dashboard/inbox';
            handleCloseNotification();
          }}
        />
      )}
      {showNotification.siaPersonalizationFailed && siaPersonalizationFailedNotification && (
        <CustomNotification
          open={showNotification.siaPersonalizationFailed}
          onClose={handleCloseNotification}
          title="‼️ SIA could not fully learn!"
          message={siaPersonalizationFailedNotification.content.message}
          actionText={
            siaPersonalizationFailedNotification.content.failureType ===
            SIA_PERSONALIZATION_FAILED_REASON.BRAND_GUIDELINES_FAILED
              ? 'Update Personalization Guidelines'
              : 'Update Website'
          }
          autoHideDuration={6000}
          instantClose={true}
          playSound={true}
          handleClickCross={() => {
            markNotificationsAsRead([siaPersonalizationFailedNotification]);
            handleCloseNotification();
          }}
          onActionClick={() => {
            if (siaPersonalizationFailedNotification) {
              markNotificationsAsRead([siaPersonalizationFailedNotification]).then(() => {
                window.open(
                  `/dashboard/settings?tab=preferences&failure_type=${siaPersonalizationFailedNotification.content.failureType}`,
                  '_blank'
                );
              });
            }

            handleCloseNotification();
          }}
        />
      )}

      {showNotification.listPersonalizationSuccess && personalizationSuccessNotification && (
        <CustomNotification
          open={showNotification.listPersonalizationSuccess}
          onClose={handleCloseNotification}
          title={`✨ ${personalizationSuccessNotification?.title}`}
          message={personalizationSuccessNotification?.content?.message}
          actionText="Review Now"
          autoHideDuration={6000}
          instantClose={true}
          playSound={true}
          handleClickCross={() => {
            markNotificationsAsRead([personalizationSuccessNotification]);
            handleCloseNotification();
          }}
          onActionClick={() => {
            if (personalizationSuccessNotification) {
              markNotificationsAsRead([personalizationSuccessNotification]).then(() => {
                if (personalizationSuccessNotification?.content?.listId) {
                  window.location.href = `/dashboard/list/detail/${personalizationSuccessNotification?.content?.listId}?tab=active-creators&page=1&${SIA_PERSONAL_LINES_COMPLETED_NOTIF_URL_PARAM}=true`;
                }

                if (personalizationSuccessNotification?.content?.campaignId) {
                  window.location.href = `/dashboard/email-templates/${personalizationSuccessNotification?.content?.campaignId}?${SIA_PERSONAL_LINES_COMPLETED_NOTIF_URL_PARAM}=true`;
                }

                if (personalizationSuccessNotification?.content?.threadIds) {
                  window.location.href = `/dashboard/inbox?${SIA_FOLLOWUP_COMPLETED_NOTIF_URL_PARAM}=true&notificationId=${personalizationSuccessNotification?._id}`;
                }
              });
            }

            handleCloseNotification();
          }}
        />
      )}

      {showNotification.sathiMigrationInterrupted && sathiMigrationInterruptedNotification && (
        <SathiMigrationInterruptedCustomNotification
          open={showNotification.sathiMigrationInterrupted}
          onClose={handleCloseNotification}
          notification={sathiMigrationInterruptedNotification}
          onMarkAsRead={markNotificationsAsRead}
        />
      )}

      {showNotification.sathiMigrationCompleted && sathiMigrationCompletedNotification && (
        <SathiMigrationCompletedCustomNotification
          open={showNotification.sathiMigrationCompleted}
          onClose={handleCloseNotification}
          notification={sathiMigrationCompletedNotification}
          onMarkAsRead={markNotificationsAsRead}
        />
      )}
    </>
  );
}
